import { NgDompurifySanitizer } from '@tinkoff/ng-dompurify';
import {
  TUI_ALERT_POSITION,
  TUI_SANITIZER,
  TuiAlertModule,
  TuiDialogModule,
  TuiFlagPipeModule,
  tuiNotificationOptionsProvider,
  tuiNumberFormatProvider,
  TuiRootModule,
  tuiSvgOptionsProvider,
  tuiSvgSrcInterceptors,
} from '@taiga-ui/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';

import {
  CUSTOM_TIME_DIFF_GENERATOR,
  TimeDiffGenerator,
  TimePastPipe,
} from 'ng-time-past-pipe';

import {
  getFutureDiffString,
  getNowString,
  getPastDiffString,
} from './time-diffs';

import { AuthService } from '@asp/authentication';
import { Observable } from 'rxjs';
import { CoreModule } from '@asp/core';
import { PageNotFoundComponent } from '@asp/pages';
import { LayoutsModule } from '@asp/layouts';
import { TUI_DATE_SEPARATOR, TuiSafeHtml } from '@taiga-ui/cdk';
import {
  TUI_DATE_TIME_VALUE_TRANSFORMER,
  TUI_DATE_VALUE_TRANSFORMER,
  TUI_VALIDATION_ERRORS,
  TuiProgressModule,
} from '@taiga-ui/kit';
import { POLYMORPHEUS_CONTEXT } from '@tinkoff/ng-polymorpheus';
import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import {
  StrawberryDateTimeTransformer,
  StrawberryDateTransformer,
} from '@asp/directives';
import { take, tap } from 'rxjs/operators';

export const timeDiffGenerator: TimeDiffGenerator = (diff): string => {
  if (diff.seconds === 0) {
    return getNowString(diff);
  }
  return diff.isFuture ? getFutureDiffString(diff) : getPastDiffString(diff);
};

function appInitFactory(auth: AuthService): () => Observable<any> {
  //return () => auth.init().pipe(delay(1000000));
  return () =>
    auth.optimisticWriteCurrentUser().pipe(
      //delay(10000),
      tap(() => auth.init().pipe(take(1)).subscribe()),
    );
}

// noinspection JSUnusedGlobalSymbols
@NgModule({
  declarations: [AppComponent, PageNotFoundComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    CoreModule,
    LayoutsModule,
    TuiRootModule,
    TuiAlertModule,
    TuiDialogModule,
    TimePastPipe,
    TuiProgressModule,
    TuiFlagPipeModule,
  ],
  providers: [
    { provide: TUI_SANITIZER, useClass: NgDompurifySanitizer },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitFactory,
      deps: [AuthService],
      multi: true,
    },
    { provide: TUI_ALERT_POSITION, useValue: 'auto 2rem 3rem auto' },
    { provide: CUSTOM_TIME_DIFF_GENERATOR, useValue: timeDiffGenerator },
    tuiSvgOptionsProvider({
      contentProcessor: (content: TuiSafeHtml) => {
        let ret = String(content)
          .replace(/fill=".*?"/g, 'fill="currentColor"')
          .replace(/width=".*?"/g, 'width="100%"')
          .replace(/height=".*?"/g, 'height="100%"');
        if (!ret.includes('fill="currentColor"')) {
          ret = ret.replace(/<svg/g, '<svg fill="currentColor"');
        }
        return ret;
      },
    }),
    tuiSvgSrcInterceptors((src: any) =>
      String(src).startsWith('mat::')
        ? `assets/icons/material/${String(src).replace('mat::', '')}.svg`
        : src,
    ),
    tuiNotificationOptionsProvider({
      autoClose: 8000,
    }),
    { provide: POLYMORPHEUS_CONTEXT, useValue: undefined },
    {
      provide: TUI_VALIDATION_ERRORS,
      useValue: {
        required: $localize`This field is required`,
        email: $localize`Enter a valid email`,
        maxlength: ({ requiredLength }: { requiredLength: string }) =>
          $localize`Maximum length — ${requiredLength}`,
        minlength: ({ requiredLength }: { requiredLength: string }) =>
          $localize`Minimum length — ${requiredLength}`,
        pwdStrength: ({ errors }: { errors: string[] }) => errors.join('\n'),
        matching: $localize`Passwords do not match`,
      },
    },
    { provide: TUI_DATE_SEPARATOR, useValue: '/' },
    // TODO: Make this dynamic based on the user's locale
    tuiNumberFormatProvider({
      decimalSeparator: '.',
      thousandSeparator: ',',
    }),
    {
      provide: TUI_DATE_VALUE_TRANSFORMER,
      useClass: StrawberryDateTransformer,
    },
    {
      provide: TUI_DATE_TIME_VALUE_TRANSFORMER,
      useClass: StrawberryDateTimeTransformer,
    },
    Location,
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
